import { useEffect, useState } from 'react';
import { TopBar } from '../components/TopBar';
import { api } from '../lib/api';
import { textoDeError } from '../components/Dialogo';
import { useAuth } from '../auth/AuthContext';
import { EstadoPin, leerMiPin } from '../lib/pin';
import { ROL_ETIQUETA } from '../lib/roles';
import { telefonoLegible } from '../lib/phone';

/**
 * MI CUENTA (0.47.0): el PIN de cada quien. Se pide para lo delicado — hoy, marcar, quitar o editar una cuenta de prueba
 * y reiniciar el PIN de otra persona — sin volver a escribir la contraseña. 4 a 6 números; nunca se guarda en texto
 * (scrypt con sal en el servidor); 5 intentos fallidos lo bloquean 15 minutos. Cambiarlo pide el actual; si se olvidó,
 * otra persona administradora lo reinicia en Administración → Cuentas. Desde 0.50.0, también el teléfono de la cuenta
 * (`MiTelefono`, abajo).
 */
const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const soloDigitos = (v: string) => v.replace(/\D/g, '').slice(0, 6);

/**
 * TU TELÉFONO (0.50.0): el de tu cuenta del personal, opcional y cifrado como el correo (api/src/functions/cuenta-datos.ts).
 * Aquí cada quien ve y cambia el suyo; el de otra persona lo edita un admin en Administración → Cuentas.
 */
interface MisDatos { nombre: string; email: string | null; telefono: string | null; rol: string; tenant: string | null }
function MiTelefono() {
  const [d, setD] = useState<MisDatos | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [msg, setMsg] = useState<{ t: string; ok: boolean } | null>(null);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { api<MisDatos>('cuenta/datos').then(setD).catch((e) => setErr(textoDeError(e))); }, []);
  const guardar = async (tel: string) => {
    setGuardando(true); setMsg(null);
    try {
      const r = await api<MisDatos>('cuenta/datos', { method: 'PUT', body: JSON.stringify({ telefono: tel }) });
      setD(r); setEditando(false);
      setMsg({ t: r.telefono ? 'Listo: tu teléfono quedó guardado.' : 'Listo: tu cuenta ya no tiene teléfono.', ok: true });
    } catch (e) { setMsg({ t: textoDeError(e), ok: false }); }
    finally { setGuardando(false); }
  };
  return (
    <div className="card" style={{ padding: '14px 16px', marginTop: 12, display: 'grid', gap: 10 }} data-mi-telefono>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--gold-dark)' }}>Tu teléfono</h2>
      {err && <div style={{ color: 'var(--warning)', fontSize: 13 }}>{err}</div>}
      {d && !editando && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 14 }}>
          <span data-telefono-actual>{d.telefono ? telefonoLegible(d.telefono) : <span className="muted">Sin teléfono.</span>}</span>
          <button type="button" className="btn-ghost" style={{ fontSize: 13, padding: '4px 12px' }} onClick={() => { setValor(telefonoLegible(d.telefono)); setEditando(true); setMsg(null); }} data-cambiar-telefono>
            {d.telefono ? 'Cambiar' : 'Agregar'}</button>
        </div>
      )}
      {d && editando && (
        <form onSubmit={(e) => { e.preventDefault(); guardar(valor); }} style={{ display: 'grid', gap: 8 }} data-telefono-form>
          <label htmlFor="mi-telefono" style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            <span>Teléfono</span>
            <input id="mi-telefono" type="tel" inputMode="tel" autoComplete="tel" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="55 1234 5678" style={{ maxWidth: 260 }} />
            <span className="muted" style={{ fontSize: 12 }}>10 dígitos. Si es de otro país, empieza con + y su lada (p. ej. +1 305 555 1234). Vacío = sin teléfono.</span>
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="submit" className="btn-primary" disabled={guardando} data-guardar-telefono>{guardando ? 'Guardando…' : 'Guardar'}</button>
            <button type="button" className="btn-ghost" disabled={guardando} onClick={() => { setEditando(false); setMsg(null); }}>Cancelar</button>
          </div>
        </form>
      )}
      {msg && <div role="status" data-telefono-msg={msg.ok ? 'ok' : 'error'} style={{ fontSize: 13, color: msg.ok ? 'var(--success)' : 'var(--danger, #b91c1c)' }}>{msg.t}</div>}
      <p className="muted" style={{ margin: 0, fontSize: 12 }}>Sólo lo ven tú y administración de la clínica. No se manda a ningún otro servicio.</p>
    </div>
  );
}

/**
 * TU CONTRASEÑA (0.58.0): `PUT cuenta/contrasena { actual, nueva }`. Es la de tu cuenta: la misma para DTB HUB, Dra. IA,
 * recepción y el modo equipo de Safevity Rewards. No se manda por correo; si se olvida, administración pone una nueva en
 * Administración → Cuentas → Editar. La regla (8 a 72, no tu correo) es la del servidor (api/src/lib/cuentas.ts).
 */
function MiContrasena() {
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [otra, setOtra] = useState('');
  const [ver, setVer] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState<{ t: string; ok: boolean; campo?: string } | null>(null);
  const guardar = async (e: React.FormEvent) => {
    e.preventDefault(); setMsg(null);
    if (!actual) { setMsg({ t: 'Escribe tu contraseña actual.', ok: false, campo: 'actual' }); return; }
    if (nueva.length < 8) { setMsg({ t: 'La contraseña nueva debe tener al menos 8 caracteres.', ok: false, campo: 'nueva' }); return; }
    if (nueva !== otra) { setMsg({ t: 'Las dos contraseñas nuevas no son iguales.', ok: false, campo: 'otra' }); return; }
    setGuardando(true);
    try {
      await api('cuenta/contrasena', { method: 'PUT', body: JSON.stringify({ actual, nueva }) });
      setActual(''); setNueva(''); setOtra('');
      setMsg({ t: 'Listo: tu contraseña cambió. Úsala la próxima vez que entres a DTB HUB, Dra. IA o recepción.', ok: true });
    } catch (e2: any) { setMsg({ t: textoDeError(e2), ok: false, campo: e2?.body?.campo }); }
    finally { setGuardando(false); }
  };
  const campo = (id: string, etiqueta: string, v: string, set: (x: string) => void, auto: string, k: string) => (
    <label htmlFor={id} style={{ display: 'grid', gap: 4, fontSize: 13 }}>
      <span>{etiqueta}</span>
      <input id={id} type={ver ? 'text' : 'password'} autoComplete={auto} value={v} onChange={(e) => set(e.target.value)} maxLength={72}
        aria-invalid={msg && !msg.ok && msg.campo === k ? true : undefined} style={{ maxWidth: 300 }} data-contrasena-campo={k} />
    </label>
  );
  return (
    <div className="card" style={{ padding: '14px 16px', marginTop: 12, display: 'grid', gap: 10 }} data-mi-contrasena>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--gold-dark)' }}>Tu contraseña</h2>
      <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
        Es la misma para entrar a DTB HUB, Dra. IA y recepción. Mínimo 8 caracteres y que no sea tu correo.
      </p>
      <form onSubmit={guardar} style={{ display: 'grid', gap: 10 }} data-contrasena-form>
        {campo('pass-actual', 'Contraseña actual', actual, setActual, 'current-password', 'actual')}
        {campo('pass-nueva', 'Contraseña nueva', nueva, setNueva, 'new-password', 'nueva')}
        {campo('pass-otra', 'Escríbela otra vez', otra, setOtra, 'new-password', 'otra')}
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={ver} onChange={(e) => setVer(e.target.checked)} style={{ width: 'auto' }} data-ver-contrasena /> Mostrar lo que escribo
        </label>
        {msg && <div role="status" data-contrasena-msg={msg.ok ? 'ok' : 'error'} style={{ fontSize: 13, color: msg.ok ? 'var(--success)' : 'var(--danger, #b91c1c)' }}>{msg.t}</div>}
        <div><button type="submit" className="btn-primary" disabled={guardando} data-guardar-contrasena>{guardando ? 'Guardando…' : 'Cambiar contraseña'}</button></div>
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>¿La olvidaste? Pídele a administración que te ponga una nueva en Administración → Cuentas; después la cambias aquí. No se manda por correo.</p>
      </form>
    </div>
  );
}

export function MiCuenta() {
  const { user } = useAuth();
  const [est, setEst] = useState<EstadoPin | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [actual, setActual] = useState('');
  const [nuevo, setNuevo] = useState('');
  const [otra, setOtra] = useState('');
  const [msg, setMsg] = useState<{ t: string; ok: boolean } | null>(null);
  const [guardando, setGuardando] = useState(false);
  const cargar = () => leerMiPin().then(setEst).catch((e) => setErr(textoDeError(e)));
  useEffect(() => { cargar(); }, []);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    if (!/^\d{4,6}$/.test(nuevo)) { setMsg({ t: 'El PIN nuevo son de 4 a 6 números.', ok: false }); return; }
    if (nuevo !== otra) { setMsg({ t: 'Los dos PIN nuevos no son iguales.', ok: false }); return; }
    if (est?.tiene && !actual) { setMsg({ t: 'Escribe tu PIN actual.', ok: false }); return; }
    setGuardando(true);
    try {
      const r = await api<{ ok: boolean; cambio: boolean; estado: EstadoPin }>('cuenta/pin', { method: 'PUT', body: JSON.stringify({ nuevo, ...(est?.tiene ? { actual } : {}) }) });
      setEst(r.estado); setActual(''); setNuevo(''); setOtra('');
      setMsg({ t: r.cambio ? 'Listo: tu PIN cambió.' : 'Listo: tu PIN quedó guardado.', ok: true });
    } catch (e2) { setMsg({ t: textoDeError(e2), ok: false }); cargar(); }
    finally { setGuardando(false); }
  };

  const campo = (id: string, etiqueta: string, v: string, set: (x: string) => void, auto: string) => (
    <label htmlFor={id} style={{ display: 'grid', gap: 4, fontSize: 13 }}>
      <span>{etiqueta}</span>
      <input id={id} type="password" inputMode="numeric" autoComplete={auto} maxLength={6} value={v} onChange={(e) => set(soloDigitos(e.target.value))}
        placeholder="4 a 6 números" style={{ maxWidth: 220, letterSpacing: '0.3em', fontSize: 18 }} />
    </label>
  );

  return (
    <div>
      <TopBar />
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '18px 16px 48px' }}>
        <h1 style={{ margin: '4px 2px 8px', fontSize: 24, fontWeight: 300, color: 'var(--gold-dark)' }}>Mi cuenta</h1>
        {user && (
          <div className="card" style={{ padding: '12px 16px', fontSize: 14, display: 'grid', gap: 2 }} data-mi-cuenta>
            <b>{user.name}</b>
            <span className="muted" style={{ fontSize: 13 }}>{ROL_ETIQUETA[user.role] || user.role} · {user.tenantName}</span>
          </div>
        )}
        <MiContrasena />
        <MiTelefono />
        <div className="card" style={{ padding: '14px 16px', marginTop: 12, display: 'grid', gap: 10 }} data-mi-pin>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--gold-dark)' }}>Tu PIN</h2>
          <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
            Se pide para confirmar lo delicado, sin volver a escribir tu contraseña: marcar, quitar o editar una cuenta de prueba
            (su nivel, sus puntos, su plan o el pago de prueba), decir si una de sus ventas es real o de prueba y reiniciar el PIN de otra
            persona. También abre el modo equipo de Safevity Rewards sin escribir tu contraseña cada día. Son de 4 a 6 números y sólo tú
            lo sabes: no se guarda en texto. Cinco intentos fallidos seguidos lo bloquean 15 minutos.
          </p>
          {err && <div style={{ color: 'var(--warning)', fontSize: 13 }}>{err}</div>}
          {est && (
            <div style={{ fontSize: 13.5 }} data-pin-estado={est.bloqueadoHasta ? 'bloqueado' : est.tiene ? 'tiene' : 'sin'}>
              {est.bloqueadoHasta
                ? <span style={{ color: 'var(--warning)' }}>Bloqueado por intentos fallidos hasta {fecha(est.bloqueadoHasta)}.</span>
                : est.tiene
                  ? <>Ya tienes PIN{est.actualizado ? ` (desde ${fecha(est.actualizado)})` : ''}.{est.intentos ? ` Llevas ${est.intentos} intento${est.intentos === 1 ? '' : 's'} fallido${est.intentos === 1 ? '' : 's'}.` : ''}</>
                  : <strong>Todavía no tienes PIN.</strong>}
            </div>
          )}
          {est && (
            <form onSubmit={guardar} style={{ display: 'grid', gap: 10 }} data-pin-form>
              {est.tiene && campo('pin-actual', 'PIN actual', actual, setActual, 'current-password')}
              {campo('pin-nuevo', est.tiene ? 'PIN nuevo' : 'Tu PIN', nuevo, setNuevo, 'new-password')}
              {campo('pin-otra', 'Escríbelo otra vez', otra, setOtra, 'new-password')}
              {msg && <div role="status" data-pin-msg={msg.ok ? 'ok' : 'error'} style={{ fontSize: 13, color: msg.ok ? 'var(--success)' : 'var(--danger, #b91c1c)' }}>{msg.t}</div>}
              <div><button type="submit" className="btn-primary" disabled={guardando} data-guardar-pin>{guardando ? 'Guardando…' : est.tiene ? 'Cambiar PIN' : 'Guardar PIN'}</button></div>
              <p className="muted" style={{ margin: 0, fontSize: 12 }}>¿Lo olvidaste? Pídele a otra persona administradora que lo reinicie en Administración → Cuentas; después pones uno nuevo aquí.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
